// ✅ Optimized card-product-slider.js + segmented bottom counter
// - Lazy-init Splide when near viewport
// - Single, batched ResizeObserver
// - Debounced MutationObserver scoped to product grid changes
// - Desktop hover preview (instant jump to 2nd image)
// - Custom arrows (instant move)
// - Segmented line counter that shows on card hover and scales with slide count

(function () {
  // ---------- Config ----------
  const IO_ROOT_MARGIN = '200px 0px';
  const HOVER_INSTANT_INDEX = 1;
  const LEAVE_INSTANT_INDEX = 0;
  const DEFAULT_SPEED = 600;

  // ---------- Environment flags ----------
  const isTouch =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const baseSpeed = reduceMotion ? 0 : DEFAULT_SPEED;

  // ---------- Registries ----------
  const roTargets = new Set();

  // ---------- Global ResizeObserver (batched with rAF) ----------
  let roRaf = null;
  const globalRO = new ResizeObserver(() => {
    if (roRaf) return;
    roRaf = requestAnimationFrame(() => {
      roRaf = null;
      roTargets.forEach((splide) => {
        if (splide?.root?.isConnected) {
          try { splide.refresh(); } catch (_) {}
        }
      });
    });
  });

  // ---------- IntersectionObserver for lazy mount ----------
  const lazyIO = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const slider = e.target;
      lazyIO.unobserve(slider);
      if (!slider.classList.contains('splide-initialized')) {
        mountSlider(slider);
      }
    }
  }, { rootMargin: IO_ROOT_MARGIN });

  // ---------- Helpers ----------
  function withInstantMove(splide, fn) {
    const prevSpeed = splide.options.speed;
    splide.options = { ...splide.options, speed: 0 };
    try { fn(); } finally {
      splide.options = { ...splide.options, speed: prevSpeed };
    }
  }

  // Load deferred images (images with data-src that haven't been loaded yet)
  function loadDeferredImages(slider) {
    const deferredImages = slider.querySelectorAll('.card-product-image--deferred[data-src]');
    deferredImages.forEach((img) => {
      if (img.src && !img.src.startsWith('data:')) return; // Already loaded
      const src = img.getAttribute('data-src');
      const srcset = img.getAttribute('data-srcset');
      if (src) {
        img.src = src;
        img.removeAttribute('data-src');
      }
      if (srcset) {
        img.srcset = srcset;
        img.removeAttribute('data-srcset');
      }
    });
  }

  function addDesktopHover(card, splide) {
    if (!card || isTouch) return;
    let imagesLoaded = false;
    card.addEventListener('mouseenter', () => {
      // Load deferred images on first hover
      if (!imagesLoaded) {
        const slider = card.querySelector('.card-product-splide');
        if (slider) loadDeferredImages(slider);
        imagesLoaded = true;
      }
      if ((splide.length || 0) > 1) {
        withInstantMove(splide, () => splide.go(HOVER_INSTANT_INDEX));
      }
    });
    card.addEventListener('mouseleave', () => {
      if ((splide.length || 0) > 1) {
        withInstantMove(splide, () => splide.go(LEAVE_INSTANT_INDEX));
      }
    });
  }

  // ---- Segmented bottom counter (build + update) ----
  function buildCounter(wrapper, total) {
    // wrapper is .card__inner
    if (!wrapper || total <= 1) return null;

    // Avoid duplicates
    let bar = wrapper.querySelector('.cp-counter');
    if (bar) return bar;

    bar = document.createElement('div');
    bar.className = 'cp-counter'; // CSS controls visibility on hover
    const track = document.createElement('div');
    track.className = 'cp-counter__track';
    bar.appendChild(track);

    // Create N segments
    for (let i = 0; i < total; i++) {
      const seg = document.createElement('span');
      seg.className = 'cp-counter__seg';
      track.appendChild(seg);
    }

    wrapper.appendChild(bar);
    return bar;
  }

  function updateCounter(wrapper, total, activeIndex) {
    if (!wrapper || total <= 1) return;
    const track = wrapper.querySelector('.cp-counter__track');
    if (!track) return;
    const segs = track.children;
    const idx = ((activeIndex % total) + total) % total;
    for (let i = 0; i < segs.length; i++) {
      segs[i].classList.toggle('is-active', i === idx);
    }
  }

  function addCustomArrows(wrapper, splide, total) {
    if (!wrapper) return;
    const prevArrow = wrapper.querySelector('.splide-arrow-prev');
    const nextArrow = wrapper.querySelector('.splide-arrow-next');
    if (prevArrow) {
      prevArrow.addEventListener('click', () => {
        withInstantMove(splide, () => splide.go('<'));
      });
    }
    if (nextArrow) {
      nextArrow.addEventListener('click', () => {
        withInstantMove(splide, () => splide.go('>'));
      });
    }
  }

  // ---------- Mount a single slider ----------
  function mountSlider(slider) {
    if (slider.classList.contains('splide-initialized')) return;

    slider.classList.add('splide-initialized');

    const prevVis = slider.style.visibility;
    slider.style.visibility = 'hidden';

    let deferredLoaded = false;
    const splide = new Splide(slider, {
      type: 'loop',
      drag: true,
      speed: baseSpeed,
      arrows: false,
      pagination: true,
    });

    // Load deferred images on first drag (touch devices)
    splide.on('drag', () => {
      if (!deferredLoaded) {
        loadDeferredImages(slider);
        deferredLoaded = true;
      }
    });

    // Build UI that depends on slide count on 'mounted'
    splide.on('mounted', () => {
      slider.style.visibility = prevVis || 'visible';

      const total = splide.length || 1;
      const wrapper = slider.closest('.card__inner');

      // Build segmented counter and set initial state
      const bar = buildCounter(wrapper, total);
      if (bar) {
        // Initial index
        const idx = splide.index % total;
        updateCounter(wrapper, total, idx);

        // Update on any movement
        splide.on('move', (newIndex) => {
          updateCounter(wrapper, total, newIndex);
        });
        splide.on('refresh', () => {
          // In case slide count changes (rare on PDP cards, common on responsive)
          const newTotal = splide.length || total;
          if (newTotal !== total) {
            // Rebuild to match new count
            const existing = wrapper.querySelector('.cp-counter');
            if (existing) existing.remove();
            buildCounter(wrapper, newTotal);
          }
          updateCounter(wrapper, splide.length || newTotal, splide.index);
        });
      }

      // Custom arrows (instant)
      addCustomArrows(wrapper, splide);
    });

    try {
      splide.mount();
    } catch (e) {
      slider.style.visibility = prevVis || '';
      throw e;
    }

    // Desktop hover image preview
    const card = slider.closest('.card-wrapper');
    addDesktopHover(card, splide);

    // Track for global ResizeObserver refresh
    roTargets.add(splide);
    globalRO.observe(slider);
  }

  // ---------- Initialize: find new sliders and lazy-observe them ----------
  function initCardProductSplides() {
    const sliders = document.querySelectorAll('.card-product-splide:not(.splide-initialized)');
    if (!sliders.length) return;
    sliders.forEach((slider) => lazyIO.observe(slider));
  }

  // ---------- Shopify lifecycle events ----------
  document.addEventListener('DOMContentLoaded', initCardProductSplides);
  document.addEventListener('shopify:section:load', initCardProductSplides);
  document.addEventListener('facets:updated', initCardProductSplides);

  // ---------- Debounced MutationObserver on #product-grid ----------
  const productGridContainer = document.querySelector('#product-grid');
  if (productGridContainer) {
    let moTimer = null;
    const DEBOUNCE_MS = 50;

    new MutationObserver((mutations) => {
      let needInit = false;
      for (const m of mutations) {
        if (m.type !== 'childList' || !m.addedNodes?.length) continue;
        for (const n of m.addedNodes) {
          if (n.nodeType !== 1) continue;
          if (n.matches?.('.card-product-splide') || n.querySelector?.('.card-product-splide')) {
            needInit = true; break;
          }
        }
        if (needInit) break;
      }
      if (!needInit) return;
      clearTimeout(moTimer);
      moTimer = setTimeout(initCardProductSplides, DEBOUNCE_MS);
    }).observe(productGridContainer, { childList: true, subtree: true });
  }
})();